import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (!window.__dwellInitialProductImageSet) {
      window.__dwellInitialProductImageSet = true;

      requestAnimationFrame(() => {
        this.#selectInitialMedia();
      });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);

    setTimeout(() => {
      newMediaGallery.selectVariantMedia();
    }, 50);
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  selectVariantMedia() {
    this.#selectMediaById(this.dataset.selectedVariantMediaId);
  }

  #selectInitialMedia() {
    const index = Number(this.dataset.initialMediaIndex);
    if (Number.isNaN(index) || !this.slideshow) return;

    this.slideshow.select(index, undefined, { animate: false });
  }

  #selectMediaById(mediaId) {
    if (!mediaId || !this.slideshow) return;

    const slides = Array.from(this.slideshow.querySelectorAll('[data-media-id]'));
    const mediaIndex = slides.findIndex((item) => item.dataset.mediaId === String(mediaId));

    if (mediaIndex >= 0) {
      this.slideshow.select(mediaIndex, undefined, { animate: false });
    }
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}